import { AdminScreenView } from "../components/AdminScreenView";
import { UserSearchSelect } from "../components/SearchSelect";
import { AnonymousUserWithoutPlan, DeglazeUser, getUserUuid, HouseholdId, PremiumVia } from "@eatbetter/users-shared";
import { useEffect, useState } from "react";
import { View } from "react-native";
import { Divider } from "antd";
import { useStringParam } from "../lib/ParamUtils";
import { Link, useNavigate } from "react-router-dom";
import {
  displayUnexpectedErrorAndLog,
  THeading1,
  THeading2,
  Spacer,
  TBody,
  Photo,
  getSuggestedSourceSize,
  CurrentEnvironment,
  Pressable,
  FlexedSpinner,
} from "@eatbetter/ui-shared";
import { getUserOverview, setUserFullAccess } from "../lib/users/AdminUserThunks";
import {
  addMilliseconds,
  daysBetween,
  defaultTimeProvider,
  getDurationInMilliseconds,
  newId,
  UserId,
} from "@eatbetter/common-shared";
import { useDispatch } from "../lib/AdminRedux";
import { AdminUserRecipesScreenNav } from "./AdminUserRecipesScreen";
import { TableFromObject } from "../components/TableFromObject";
import { AdminUserDevice, AdminUserOverview } from "@eatbetter/composite-shared";
import { UserSearchStatus } from "@eatbetter/search-shared";
import { AdminUserSocialScreenNav } from "./AdminUserSocialScreen";
import { colorPalette } from "@eatbetter/ui-shared/src/components/themes/Colors";

export const AdminUsersScreenEmptyNav = {
  getPath: () => "/users",
};

const userIdParam = ":userId";
export const AdminUsersScreenIdNav = {
  getPath: (id?: string) => {
    return `${AdminUsersScreenEmptyNav.getPath()}/${id ?? userIdParam}`;
  },
};

export const AdminUsersScreen = () => {
  const idParam = useStringParam(userIdParam).optional();
  const [searchUser, setSearchUser] = useState<DeglazeUser | AnonymousUserWithoutPlan | undefined>(undefined);
  const [userDetails, setUserDetails] = useState<AdminUserOverview | undefined>(undefined);
  const [refreshId, setRefreshId] = useState("");
  const [waiting, setWaiting] = useState(false);
  const nav = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!idParam) {
      setSearchUser(undefined);
      setUserDetails(undefined);
    } else {
      dispatch(getUserOverview(idParam as UserId))
        .then(res => {
          if (searchUser?.userId !== res.user.userId) {
            // if the page is loaded without the select being populated, we need to populate it
            setSearchUser(res.user);
          }

          setUserDetails(res);
        })
        .catch(err => {
          displayUnexpectedErrorAndLog("Error in AdminUsersScreen useEffect getUser", err);
        });
    }
  }, [idParam, refreshId]);

  useEffect(() => {
    if (!searchUser && idParam) {
      nav(AdminUsersScreenEmptyNav.getPath());
    }

    if (searchUser && searchUser.userId !== idParam) {
      nav(AdminUsersScreenIdNav.getPath(searchUser.userId));
    }
  }, [searchUser]);

  const onToggleFullAccess = async () => {
    if (!userDetails?.user || !userDetails.user.isRegistered) {
      return;
    }

    try {
      const hasAccess = !!userDetails.user.fa;
      await dispatch(setUserFullAccess({ userId: userDetails.user.userId, access: hasAccess ? null : {} }, setWaiting));
      setRefreshId(newId());
    } catch (err) {
      displayUnexpectedErrorAndLog("Error toggling user full access on admin users page", err);
    }
  };

  return (
    <AdminScreenView>
      <View style={{ width: 400 }}>
        <UserSearchSelect mode="single" selected={searchUser} onChange={setSearchUser} />
      </View>
      <Divider />
      {waiting && <FlexedSpinner />}
      {!waiting && <UserDetail data={userDetails} onToggleFullAccess={onToggleFullAccess} />}
    </AdminScreenView>
  );
};

const UserDetail = (props: { data?: AdminUserOverview; onToggleFullAccess: () => Promise<void> }) => {
  if (!props.data) {
    return null;
  }

  const user = props.data.user;
  const uuid = getUserUuid(user.userId);

  return (
    <View>
      <UserProps data={props.data} onToggleFullAccess={props.onToggleFullAccess} />
      {user.householdId && (
        <>
          <Spacer vertical={2} />
          <Household householdId={user.householdId} users={user.household} />
        </>
      )}
      <Spacer vertical={2} />
      <View>
        <Link to={AdminUserRecipesScreenNav.getPath(user.userId)}>User Recipes</Link>
        <Spacer vertical={0.5} />
        <Link to={AdminUserSocialScreenNav.getPath(user.userId)}>Social Info</Link>
        <Spacer vertical={0.5} />
        <a href={`${getMixpanelUrl()}/profile#distinct_id=${user.userId}`} target="_blank" rel="noreferrer">
          Mixpanel
        </a>
        <Spacer vertical={0.5} />
        <a
          target="_blank"
          href={`https://app.datadoghq.com/logs?query=%40trace.userId%3A${
            user.userId
          }&cols=%40service%2C%40routeKey%2C%40status%2C%40responseLatency&fromUser=true&index=%2A&messageDisplay=inline&refresh_mode=sliding&stream_sort=desc&viz=stream&from_ts=${addMilliseconds(
            defaultTimeProvider(),
            -1 * getDurationInMilliseconds({ days: 15 })
          )}&live=true`}
          rel="noreferrer"
        >
          Logs
        </a>
        <Spacer vertical={0.5} />
        <a
          target="_blank"
          href={`https://app.revenuecat.com/projects/226bc8e4/customers/${encodeURIComponent(uuid)}`}
          rel="noreferrer"
        >
          RevenueCat
        </a>
      </View>
      <Spacer vertical={2} />
      <THeading2>Revenue</THeading2>
      <table>
        <tbody>
          <tr>
            <td>Plan Type</td>
            <td>{user.plan.planType}</td>
          </tr>
          {!!user.plan.via && (
            <tr>
              <td>Pro Via</td>
              <td>{getProVia(user.plan.via)}</td>
            </tr>
          )}
          {!!user.plan.paidSubscription && (
            <tr>
              <td>Paid</td>
              <td>{JSON.stringify(user.plan.paidSubscription)}</td>
            </tr>
          )}
          <tr>
            <td>Limits</td>
            <td>{JSON.stringify(user.plan.limits)}</td>
          </tr>
          <tr>
            <td style={{ width: 150 }}>UUID</td>
            <td>{uuid}</td>
          </tr>
        </tbody>
      </table>
      <Spacer vertical={2} />
      <THeading2>Recent Devices ({props.data.deviceCount})</THeading2>
      {props.data.recentDevices.length === 0 && <TBody>No devices</TBody>}
      {props.data.recentDevices.map(d => (
        <DeviceRow key={d.deviceId} device={d} />
      ))}
      <Spacer vertical={2} />
      <THeading2>Checkpoints</THeading2>
      <TableFromObject object={user.checkpoints} />
    </View>
  );
};

function getSearchStatusDisplay(s: UserSearchStatus): string {
  // indexingStatus is only meaningful when enabled
  return s.enabled ? `enabled (${s.indexingStatus})` : "disabled";
}

function getProVia(p: PremiumVia): string {
  const getExpires = () => {
    if (!p.expires) {
      return "never";
    }

    const d = new Date(p.expires);
    if (daysBetween(defaultTimeProvider(), p.expires) < 1) {
      return d.toLocaleString();
    }

    return d.toLocaleDateString();
  };

  const getVia = () => {
    if (p.type === "paid") {
      if (p.trialStatus === "active") {
        return "trial (active)";
      } else if (p.trialStatus === "cancelled") {
        return "trial (cancelled)";
      }

      return "paid";
    }

    return p.type;
  };

  return `${getVia()}, expires ${getExpires()}`;
}

const UserProps = (props: { data: AdminUserOverview; onToggleFullAccess: () => Promise<void> }) => {
  const { user } = props.data;
  if (!user.isRegistered) {
    return (
      <table>
        <tbody>
          <tr>
            <td style={{ width: 150 }}>Anonymous</td>
            <td>{String(!user.isRegistered)}</td>
          </tr>
          <tr>
            <td>User ID</td>
            <td>{user.userId}</td>
          </tr>
          {!!props.data.user.deleted && (
            <tr style={{ backgroundColor: "red" }}>
              <td>Deleted</td>
              <td>{daysBetween(props.data.user.deleted, defaultTimeProvider()).toFixed(1)} days ago</td>
            </tr>
          )}
          <tr>
            <td>Access</td>
            <td>{user.access}</td>
          </tr>
          <tr>
            <td>Created</td>
            <td>
              {new Date(user.created).toLocaleString()} ({daysBetween(defaultTimeProvider(), user.created).toFixed(1)}{" "}
              days ago)
            </td>
          </tr>
          <tr>
            <td>Search Indexing</td>
            <td>{getSearchStatusDisplay(props.data.searchStatus)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  const nameAndEmail = `${user.name} <${user.email.address}>`;

  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        {user.photo && (
          <>
            <Photo style="avatarLarge" sourceSize={getSuggestedSourceSize("avatarLarge")} source={user.photo} />
            <Spacer horizontal={1} />
          </>
        )}
        <THeading1>{user.name}</THeading1>
        <THeading2> @{user.username}</THeading2>
      </View>
      <Spacer vertical={1} />
      <table>
        <tbody>
          {!!props.data.user.deleted && (
            <tr style={{ backgroundColor: "red" }}>
              <td>Deleted</td>
              <td>{daysBetween(props.data.user.deleted, defaultTimeProvider()).toFixed(1)} days ago</td>
            </tr>
          )}
          <tr>
            <td style={{ width: 150 }}>Name</td>
            <td>{user.name}</td>
          </tr>
          <tr>
            <td>Username</td>
            <td>
              <Pressable onPress={() => navigator.clipboard.writeText(user.username)}>{user.username}</Pressable>
            </td>
          </tr>
          <tr>
            <td>Email</td>
            <td>
              <Pressable onPress={() => navigator.clipboard.writeText(nameAndEmail)}>
                {nameAndEmail} ({user.email.verified ? "verified" : "unverified"})
              </Pressable>
            </td>
          </tr>
          <tr>
            <td>User ID</td>
            <td>
              <Pressable onPress={() => navigator.clipboard.writeText(user.userId)}>{user.userId}</Pressable>
            </td>
          </tr>
          <tr>
            <td>Household ID</td>
            <td>{user.householdId ?? "-"}</td>
          </tr>
          <tr>
            <td>Access</td>
            <td>{user.access}</td>
          </tr>
          <tr>
            <td>Full Content Access</td>
            <td>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {`${user.fa ?? false}`}
                <Spacer horizontal={1} />
                <Pressable onPress={props.onToggleFullAccess}>
                  <TBody color={colorPalette.skyBlue}>{user.fa ? "revoke" : "grant"}</TBody>
                </Pressable>
              </View>
            </td>
          </tr>
          <tr>
            <td>Referred by</td>
            <td>{user.referredBy ?? "-"}</td>
          </tr>
          <tr>
            <td>Push Tokens</td>
            <td>{props.data.pushTokenCount}</td>
          </tr>
          <tr>
            <td>Devices</td>
            <td>{props.data.deviceCount}</td>
          </tr>
          <tr>
            <td>Created</td>
            <td>
              {new Date(user.created).toLocaleString()} ({daysBetween(defaultTimeProvider(), user.created).toFixed(1)}{" "}
              days ago)
            </td>
          </tr>
          <tr>
            <td>Recipe Count</td>
            <td>{props.data.recipeCount}</td>
          </tr>
          <tr>
            <td>Search Indexing</td>
            <td>{getSearchStatusDisplay(props.data.searchStatus)}</td>
          </tr>
          <tr>
            <td>Cook Count</td>
            <td>{props.data.cookCount}</td>
          </tr>
          <tr>
            <td>Follower Count</td>
            <td>{props.data.followersCount}</td>
          </tr>
          <tr>
            <td>Following Count</td>
            <td>{props.data.followingCount}</td>
          </tr>
        </tbody>
      </table>
    </View>
  );
};

const Household = (props: { householdId?: HouseholdId; users: DeglazeUser[] }) => {
  if (!props.householdId) {
    return null;
  }

  return (
    <View>
      <TBody>Household {props.householdId}</TBody>
      {props.users.map(u => {
        return (
          <Link to={AdminUsersScreenIdNav.getPath(u.userId)} target="_blank" key={u.userId}>
            {u.name}, {u.username}, {u.userId}
          </Link>
        );
      })}
    </View>
  );
};

const DeviceRow = (props: { device: AdminUserDevice }) => {
  const { device } = props;
  const lastSeen = new Date(device.lastSeen);
  const daysAgo = daysBetween(defaultTimeProvider(), device.lastSeen).toFixed(1);

  return (
    <table style={{ marginBottom: 8 }}>
      <tbody>
        <tr>
          <td style={{ width: 150 }}>Device ID</td>
          <td>{device.deviceId}</td>
        </tr>
        <tr>
          <td style={{ width: 150 }}>Platform</td>
          <td>{device.platform}</td>
        </tr>
        <tr>
          <td>Device Type</td>
          <td>{device.deviceType ?? "-"}</td>
        </tr>
        <tr>
          <td>OS Version</td>
          <td>{device.deviceOsVersion ?? "-"}</td>
        </tr>
        <tr>
          <td>App Version</td>
          <td>{device.appVersion ?? "-"}</td>
        </tr>
        <tr>
          <td>Timezone</td>
          <td>{device.timezone ?? "-"}</td>
        </tr>
        <tr>
          <td>Locale</td>
          <td>{device.locale ?? "-"}</td>
        </tr>
        <tr>
          <td>Last Seen</td>
          <td>
            {lastSeen.toLocaleString()} ({daysAgo} days ago)
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const getMixpanelUrl = () =>
  CurrentEnvironment.configEnvironment() === "prod"
    ? "https://mixpanel.com/project/2823856/view/3358020/app"
    : "https://mixpanel.com/project/2823652/view/3357824/app";
